import { Badge, Group, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import type { AssetTarget } from '@opsopti/shared'
import {
  assetTargetSchema,
  getHasManyJoins,
  getHasOneRelationshipId,
  getJoinsCount,
  useAssetTargetsQuery,
  useAssetTargetUpdateMutation,
  useGlobalState,
} from '@opsopti/shared'
import type { PayloadFindRequestParams } from '@opsopti/shared/src/services/api/api.types'
import { IconPackage, IconViewfinder } from '@tabler/icons-react'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { createMRTColumnHelper } from 'mantine-react-table'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

import type { TableProps } from '@/components/Table'
import {
  CellTextWithTooltip,
  EditTextarea,
  HeaderCellWithTooltip,
  Table,
  TableRowActionButton,
} from '@/components/Table'
import { AssetSentryTargetFormScreen } from '@/screens/AssetSentryTargetFormScreen'
import { tableScreenIcon } from '@/screens/TableScreen'
import { getFormScreenTitle, showErrorNotification, showSuccessNotification } from '@/utils/componentCommons'

import { AssetsTable } from './AssetsTable'

type AssetTargetsTableProps = {
  where?: PayloadFindRequestParams['where']
  TableProps?: Omit<TableProps<AssetTarget>, 'data' | 'columns'>
}

export const AssetTargetsTable = observer(function AssetTargetsTable(props: AssetTargetsTableProps) {
  const { uiStore } = useGlobalState()

  const targetsQuery = useAssetTargetsQuery({
    params: {
      depth: '1',
      where: props.where,
      select: {
        visibilityStatus: true,
        name: true,
        type: true,
        internalNotes: true,
        memberNotes: true,
        assetAssignments: true,
      },
    },
  })
  const targetUpdateMutation = useAssetTargetUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  function archive(target: AssetTarget) {
    modals.openConfirmModal({
      title: 'Archive Target?',
      centered: true,
      children: <Text size="sm">{`Are you sure you want to archive this Target?`}</Text>,
      labels: { confirm: 'Archive Target', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: () => targetUpdateMutation.mutate({ id: target.id, visibilityStatus: 'archived' }),
    })
  }

  return (
    <Table
      columns={targetsCols}
      data={targetsQuery.data?.docs ?? []}
      state={{ isLoading: targetsQuery.isLoading }}
      entity={{ singular: 'Assignment Target', plural: 'Assignment Targets', Icon: IconViewfinder }}
      displayColumnDefOptions={{ 'mrt-row-actions': { size: 138 } }}
      enableTableHead={!!targetsQuery.data?.docs?.length}
      // editing
      enableEditing
      editDisplayMode="cell"
      // row actions
      rowActionsInclude={['edit', 'archive']}
      rowActionsTooltipLabels={{ edit: `Edit Target`, archive: 'Archive Target' }}
      rowActionsButtonProps={row => {
        if (row.original.visibilityStatus === 'archived') {
          return { archive: { disabled: true } }
        }
      }}
      onRowActionClick={(action, row) => {
        if (action === 'archive') {
          archive(row.original)
        } else if (action === 'edit') {
          uiStore.openFormDrawer({
            Component: AssetSentryTargetFormScreen,
            ComponentProps: { params: { id: row.original.id }, goBackFn: uiStore.closeFormDrawer, type: 'drawer' },
            TitleProps: {
              Icon: tableScreenIcon['targets'],
              title: getFormScreenTitle(row.original.id, 'Target', row.original.name),
            },
          })
        }
      }}
      // toolbar
      toolbarActionsInclude={['new', 'heightToggle']}
      toolbarActionsState={{ heightToggle: uiStore.tableHeightState['targets'] }}
      onToolbarActionClick={action => {
        if (action === 'new') {
          uiStore.openFormDrawer({
            Component: AssetSentryTargetFormScreen,
            ComponentProps: { goBackFn: uiStore.closeFormDrawer, type: 'drawer' },
            TitleProps: {
              Icon: tableScreenIcon['targets'],
              title: getFormScreenTitle(null, 'Target'),
            },
          })
        } else if (action === 'heightToggle') {
          uiStore.toggleTableHeight('targets')
        }
      }}
      {...props.TableProps}
    />
  )
})

const columnHelper = createMRTColumnHelper<AssetTarget>()

const targetsCols: MRT_ColumnDef<AssetTarget>[] = [
  columnHelper.accessor('visibilityStatus', {
    header: 'Visibility',
    grow: false,
    size: 174,
    filterVariant: 'select',
    mantineFilterSelectProps: { data: Object.values(assetTargetSchema.shape.visibilityStatus.enum) },
    enableEditing: false,
    Cell: props => {
      const status = props.cell.getValue()

      return (
        <Badge
          variant="filled"
          color={
            {
              internal: 'cornflower',
              public: 'green',
              archived: 'red',
            }[status]
          }>
          {status}
        </Badge>
      )
    },
  }),
  columnHelper.accessor('type', {
    header: 'Type',
    // grow: false,
    size: 100,
    filterVariant: 'select',
    mantineFilterSelectProps: { data: Object.values(assetTargetSchema.shape.type.enum) },
    mantineTableBodyCellProps: { style: { textTransform: 'uppercase', fontWeight: 600 } },
    enableEditing: false,
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    grow: true,
    mantineTableBodyCellProps: { style: { fontWeight: 600 } },
    enableEditing: false,
  }),

  columnHelper.accessor(row => getJoinsCount(row.assetAssignments), {
    id: 'assigned',
    header: '# Assigned Assets',
    grow: false,
    enableSorting: false,
    enableEditing: false,
    enableColumnFilter: false,
    size: 160,
    Cell: ({ cell, row }) => {
      const { uiStore } = useGlobalState()

      const v = cell.getValue()

      const assigned = getHasManyJoins(row.original.assetAssignments)
      const assetIds = assigned.map(a => getHasOneRelationshipId(a.asset)).filter(v => v !== null)

      useEffect(() => {
        uiStore.updateTableModal({
          id: row.original.id,
          ComponentProps: { where: { id: assetIds.length ? { in: assetIds } : { exists: false } } },
        })
      }, [assetIds.length])

      return (
        <Group align="center" gap="xs" wrap="nowrap">
          <TableRowActionButton
            Icon={IconPackage}
            action="view"
            tooltipLabel={!!v ? 'View Assigned Assets' : undefined}
            color="port"
            actionButtonProps={{ disabled: !!!v }}
            onClick={() => {
              uiStore.openTableModal({
                id: row.original.id,
                Component: AssetsTable,
                ComponentProps: {
                  where: { id: { in: assetIds } },
                  TableProps: { enableTopToolbar: false },
                },
                TitleProps: {
                  title: `Assets Currently In Target “${row.original.name}”`,
                  Icon: tableScreenIcon['assets'],
                },
              })
            }}
          />
          <Text size="sm">{cell.getValue()}</Text>
        </Group>
      )
    },
  }),
  columnHelper.accessor('internalNotes', {
    header: 'Notes For Supervisors',
    grow: true,
    minSize: 208,
    maxSize: 250,
    enableSorting: false,
    filterVariant: 'checkbox',
    mantineFilterCheckboxProps: { label: 'Has Notes?' },
    filterFn: (row, _cId, value) => {
      if (value === 'true') {
        return !!row.original.internalNotes
      }

      if (value === 'false') {
        return !row.original.internalNotes
      }

      return true
    },
    Cell: ({ cell }) => <CellTextWithTooltip cell={cell} />,
    Header: ({ column }) => (
      <HeaderCellWithTooltip
        column={column}
        tooltip="Notes that can be used for internal purposes. Only visible to Supervisors."
      />
    ),
    Edit: props => {
      const mutation = useAssetTargetUpdateMutation({
        onErrorNotification: showErrorNotification,
        onSuccessNotification: showSuccessNotification,
      })

      return (
        <EditTextarea
          {...props}
          updateFn={v => mutation.mutateAsync({ id: props.row.original.id, internalNotes: v })}
        />
      )
    },
  }),
  columnHelper.accessor('memberNotes', {
    header: 'Notes For Members',
    grow: true,
    maxSize: 250,
    minSize: 190,
    enableSorting: false,
    filterVariant: 'checkbox',
    mantineFilterCheckboxProps: { label: 'Has Notes?' },
    filterFn: (row, _cId, value) => {
      if (value === 'true') {
        return !!row.original.memberNotes
      }

      if (value === 'false') {
        return !row.original.memberNotes
      }

      return true
    },
    Cell: ({ cell }) => <CellTextWithTooltip cell={cell} />,
    Header: ({ column }) => <HeaderCellWithTooltip column={column} tooltip="Notes displayed publicly to Members." />,
    Edit: props => {
      const mutation = useAssetTargetUpdateMutation({
        onErrorNotification: showErrorNotification,
        onSuccessNotification: showSuccessNotification,
      })

      return (
        <EditTextarea {...props} updateFn={v => mutation.mutateAsync({ id: props.row.original.id, memberNotes: v })} />
      )
    },
  }),
]
